import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowDown } from 'lucide-react';


gsap.registerPlugin(ScrollTrigger);

const Hero = () => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const titleRef = useRef(null);
  const subtextRef = useRef(null);
  const ctaRef = useRef(null);
  const lineRef = useRef(null);
  const petalsRef = useRef([]);

  useEffect(() => {
    // Ensure video plays on Apple devices
    if (videoRef.current) {
      videoRef.current.defaultMuted = true;
      videoRef.current.muted = true;
      videoRef.current.play().catch(err => {
        console.warn("Video autoplay failed:", err);
      });
    }

    const tl = gsap.timeline();

    // Text Reveal Animation
    tl.fromTo(titleRef.current,
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 1.5, ease: "power3.out" }
    )
      .fromTo(subtextRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.5, ease: "power3.out" },
        "-=1"
      )
      .fromTo(ctaRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.5, ease: "power3.out" },
        "-=1.2"
      );

    // Energy Line Animation (Flowing effect)
    gsap.to(lineRef.current, {
      strokeDashoffset: 0,
      duration: 3,
      ease: "power2.inOut",
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top center",
      }
    });

    // Petals Animation
    petalsRef.current.forEach((petal, i) => {
      // Randomize start properties
      const startX = Math.random() * 100; // vw
      const startScale = 0.5 + Math.random() * 0.5;
      const duration = 15 + Math.random() * 20;
      const delay = Math.random() * 20;

      gsap.set(petal, {
        x: `${startX}vw`,
        y: -100,
        scale: startScale,
        rotation: Math.random() * 360,
        opacity: 0.4 + Math.random() * 0.4
      });

      // Falling motion
      gsap.to(petal, {
        y: '110vh',
        rotation: '+=360',
        duration: duration,
        delay: delay,
        ease: 'none',
        repeat: -1
      });

      // Swaying motion (independent)
      gsap.to(petal, {
        x: `+=${Math.random() * 20 - 10}vw`, // Sway left/right by up to 10vw
        duration: 3 + Math.random() * 4,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
        delay: delay
      });
    });

  }, []);

  // Generate petals array
  const petals = Array.from({ length: 15 });

  return (
    <section ref={containerRef} className="relative h-screen flex flex-col justify-center items-center text-center overflow-hidden px-6">

      <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          poster="/Hero-BG.png"
          preload="auto"
          className="absolute w-full h-full object-cover transition-opacity duration-1000 ease-in-out"
        >
          {/* Use MP4 first for better compatibility with Apple devices */}
          <source src="https://cdn.pixabay.com/video/2022/03/05/109726-685086332_large.mp4" type="video/mp4" />
          <source src="/Hero-Video.mp4" type="video/mp4" />
          {/* <source src="https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.webm" type="video/webm" /> */}
          Your browser does not support the video tag.
        </video>

        {/* Vibrancy update: Removed gloomy layers, added brightness/saturation boost */}
        {/* Increased brightness to 125 and removed global black overlays to fix "too dark" issue */}
        <div className="absolute inset-0 bg-forest-green/10 mix-blend-overlay"></div>
        <div className="absolute inset-0 backdrop-saturate-[1.3] backdrop-brightness-125"></div>

        {/* Soft gradient only at the very bottom for the CTA/scroll, keeping center/top bright */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-80"></div>
      </div>

      {/* Abstract Energy Line Background */}
      <svg className="absolute w-full h-full top-0 left-0 pointer-events-none opacity-20" viewBox="0 0 1440 800" fill="none" preserveAspectRatio="none">
        <path
          ref={lineRef}
          d="M0,400 C240,400 480,200 720,400 C960,600 1200,400 1440,400"
          stroke="#FFF"
          strokeWidth="2"
          strokeDasharray="1500"
          strokeDashoffset="1500"
        />
      </svg>

      <div className="z-10 max-w-4xl flex flex-col items-center">
        {/* Stronger drop-shadow to ensure text pops against light video areas */}
        <h1 ref={titleRef} className='text-lg md:text-7xl font-rosarina font-extrabold tracking-wider text-white mb-6 leading-tight interactive drop-shadow-[0_5px_5px_rgba(0,0,0,0.8)]'>Land Healing</h1>

        <p ref={subtextRef} className="text-lg md:text-xl text-white font-sans max-w-2xl mx-auto interactive mb-10 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
          Restoring Harmony to the Land and Balance to Your Life.<br className="hidden md:block" /> 
        </p>

        <div ref={ctaRef}>
          <a
            href="#about"
            onClick={(e) => {
              e.preventDefault();
              const element = document.getElementById('about');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="group relative inline-flex items-center justify-center px-8 py-3 overflow-hidden font-medium tracking-tighter text-white bg-white/10 backdrop-blur-sm rounded-full border border-white/30 transition-all duration-300 hover:bg-white/20 hover:scale-105 hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
          >
            <span className="relative z-10 font-sans tracking-wide">Begin Your Journey</span>
          </a>
        </div>
      </div>

      {/* <div className="absolute bottom-10 animate-bounce cursor-pointer z-10" onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}>
        <span className="text-sm tracking-widest mb-2 block font-medium text-white">SCROLL</span>
        <ArrowDown className="mx-auto text-white w-5 h-5" />
      </div> */}
    </section>
  );
};

export default Hero;
